import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import { Button, TextField } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { AuthBackground } from '@/components/AuthBackground.js'
import { isUserCancelled } from '@/lib/passkeys.js'

/**
 * Shown by AuthGate whenever there is no valid session — a fresh visit, an
 * expired session, or one signed out elsewhere. Never reachable for the
 * customer portal (`/p/:token`), which sits outside AuthGate entirely.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const loginMutation = useMutation({
    mutationFn: () => api.auth.login({ username, password }),
    onSuccess: () => onSignedIn(),
  })

  // Same success path as the password form: the session cookie is set by the
  // server, then AuthGate re-reads /auth/me and handles mustChangePassword.
  const passkeyMutation = useMutation({
    mutationFn: async () => {
      const optionsJSON = await api.auth.passkeyLoginOptions()
      const response = await startAuthentication({ optionsJSON })
      return api.auth.passkeyLoginVerify(response)
    },
    onSuccess: () => onSignedIn(),
  })

  const passkeySupported = browserSupportsWebAuthn()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password || loginMutation.isPending) return
    loginMutation.mutate()
  }

  // The server deliberately gives the same message for "no such user" and
  // "wrong password" — see lib/errors.ts invalidCredentials(). Any other
  // failure (network, 500) still gets that same friendly line rather than a
  // raw error: there is nothing more specific a signed-out visitor can act on.
  const passwordError = loginMutation.isError
    ? loginMutation.error instanceof ApiError
      ? loginMutation.error.message
      : t('auth.invalidCredentials')
    : null

  // Cancelling the device's own prompt is a choice, not a failure: show nothing.
  const passkeyError =
    passkeyMutation.isError && !isUserCancelled(passkeyMutation.error)
      ? passkeyMutation.error instanceof ApiError
        ? passkeyMutation.error.message
        : t('auth.passkeyFailed')
      : null

  const errorMessage = passwordError ?? passkeyError

  return (
    <AuthBackground>
      {/* Dark glass over the photo, not the app's theme-driven Card: this
          panel always looks the same regardless of light/dark mode, because
          the photo behind it does too. */}
      <div className="w-full max-w-sm rounded-xl border border-white/15 bg-black/40 backdrop-blur-md shadow-elev-3 p-6 animate-[fade-in_900ms_ease]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <h1 className="text-title-lg text-white">{t('auth.signIn')}</h1>

          <TextField
            label={t('auth.username')}
            value={username}
            onChange={setUsername}
            autoComplete="username"
            autoFocus
            fullWidth
          />
          <TextField
            label={t('auth.password')}
            value={password}
            onChange={setPassword}
            type="password"
            autoComplete="current-password"
            fullWidth
          />

          {errorMessage && (
            <p className="text-body-sm text-error" role="alert">
              {errorMessage}
            </p>
          )}

          <Button
            type="submit"
            variant="filled"
            fullWidth
            loading={loginMutation.isPending}
            disabled={!username.trim() || !password}
          >
            {loginMutation.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>

        {passkeySupported && (
          <button
            type="button"
            onClick={() => passkeyMutation.mutate()}
            disabled={passkeyMutation.isPending || loginMutation.isPending}
            className="mt-4 w-full h-9 px-4 rounded-full border border-white/30 text-white text-label-lg interactive hover:bg-white/10 active:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {passkeyMutation.isPending ? t('auth.signingIn') : t('auth.signInWithPasskey')}
          </button>
        )}
      </div>
    </AuthBackground>
  )
}
